import React from 'react';
import { ExternalLink } from 'lucide-react';
import { PlatformIcon } from '../common/PlatformIcon';
import { SocialProfile, Platform } from '../../types';

const getPlatformStyles = (platform: string | undefined, isSelected: boolean): string => {
  const baseStyles =
    'w-12 h-12 flex items-center justify-center rounded-full text-white transition-all outline-none';

  if (!isSelected) {
    return `${baseStyles} bg-[#6A6A6A] hover:bg-[#5A5A5A]`;
  }

  const selectedStyles = `${baseStyles} scale-[0.95]`;

  const platformColors: Record<string, string> = {
    facebook: 'bg-[#1877F2] hover:bg-[#0d6efd]',
    x: 'bg-black hover:bg-black',
    twitter: 'bg-black hover:bg-black',
    threads: 'bg-black hover:bg-[#14171A]',
    instagram: 'bg-gradient-to-r from-[#f09433] via-[#dc2743] to-[#bc1888] hover:opacity-90',
    reddit: 'bg-[#FF4500] hover:bg-[#FF5700]',
    bluesky: 'bg-[#0560FF] hover:bg-[#0051F2]',
    whatsapp: 'bg-[#25D366] hover:bg-[#128C7E]',
    linkedin: 'bg-[#0A66C2] hover:bg-[#004182]',
    youtube: 'bg-[#FF0000] hover:bg-[#CC0000]',
    tiktok: 'bg-black hover:bg-[#14171A]',
    snapchat: 'bg-[#FFFC00] hover:bg-[#FFE600]',
  };

  return `${selectedStyles} ${platformColors[platform?.toLowerCase() || ''] || 'bg-[#6A6A6A]'}`;
};

interface SocialPlatformToggleProps {
  profiles?: SocialProfile[];
  selectedProfiles?: Set<string>;
  onProfileChange?: (profiles: Set<string>) => void;
  sx?: any;
}

export const SocialPlatformToggle: React.FC<SocialPlatformToggleProps> = ({
  profiles = [],
  selectedProfiles = new Set(),
  onProfileChange,
  sx,
}) => {
  // Filter to only active profiles
  const activeProfiles = profiles.filter((profile) => profile.isActive);

  // Function to get profile URL
  const getProfileUrl = (
    platform: Platform | string,
    profileId: string,
    profileName: string,
    profileLink: string | undefined
  ): string => {
    const strippedProfile = profileId.replace('@', '');
    const strippedProfileName = profileName.replace('@', '');
    const baseUrls: Record<string, string> = {
      facebook: profileLink || `https://www.facebook.com/${strippedProfile}`,
      instagram: profileLink || `https://www.instagram.com/${strippedProfile}`,
      x: profileLink || `https://x.com/${strippedProfile}`,
      threads: profileLink || `https://www.threads.net/@${strippedProfileName}`,
      reddit: profileLink || `https://www.reddit.com/user/${strippedProfile}`,
      bluesky: profileLink || `https://bsky.app/profile/${strippedProfile}`,
      whatsapp: profileLink || `https://wa.me/${strippedProfile}`,
      linkedin: profileLink || `https://www.linkedin.com/in/${strippedProfile}`,
      youtube: profileLink || `https://www.youtube.com/channel/${strippedProfile}`,
      tiktok: profileLink || `https://www.tiktok.com/@${strippedProfileName}`,
      snapchat: profileLink || `https://www.snapchat.com/add/${strippedProfile}`,
    };
    return baseUrls[platform?.toLowerCase()] || `#`;
  };

  const handleSeeProfile = (profile: SocialProfile, event: React.MouseEvent) => {
    event.stopPropagation();
    const url = getProfileUrl(
      profile.platform,
      profile.profileId || '',
      profile.profileName,
      profile.profileLink
    );
    window.open(url, '_blank');
  };

  // If no profiles are available, show message
  if (activeProfiles.length === 0) {
    return (
      <div className="p-4 bg-white dark:bg-core-dark rounded border border-dashed border-warning text-warning text-center">
        <p className="mb-2 font-medium">No Social Profiles Connected</p>
        <p className="text-sm text-gray-400">
          Please connect at least one social profile in the Account Settings to start posting.
        </p>
      </div>
    );
  }

  const handleProfileToggle = (profileId: string) => {
    if (!onProfileChange) return;

    const newSelected = new Set(selectedProfiles);
    if (newSelected.has(profileId)) {
      // Don't allow deselecting if it's the only selected profile
      if (newSelected.size === 1) {
        return;
      }
      newSelected.delete(profileId);
    } else {
      newSelected.add(profileId);
    }
    onProfileChange(newSelected);
  };

  return (
    <div className="flex gap-2 flex-wrap justify-start">
      {activeProfiles.map((profile) => {
        const isSelected = selectedProfiles.has(profile.id);
        return (
          <div key={profile.id} className="flex flex-col items-center gap-2 w-[90px] flex-shrink-0">
            <div className="relative">
              <button
                onClick={() => handleProfileToggle(profile.id)}
                aria-label={`${profile.platform} - ${profile.profileName}`}
                className={getPlatformStyles(profile.platform?.toLowerCase(), isSelected)}
              >
                <PlatformIcon platform={profile.platform} size={32} />
              </button>
              <button
                onClick={(e) => handleSeeProfile(profile, e)}
                className="absolute -top-1 -right-1 w-4 h-4 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity shadow-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                aria-label={`Open ${profile.profileName} profile`}
              >
                <ExternalLink className="w-9" />
              </button>
            </div>
            <span className="text-sm font-medium text-white text-center w-full truncate">
              {profile.profileName}
            </span>
          </div>
        );
      })}
    </div>
  );
};
